import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Download, Plus, Images, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import PhotoManagerDialog from "@/components/PhotoManagerDialog";
import ProfileCard from "@/components/ProfileCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errText, formatPrice, propertyImage, BACKEND_URL } from "@/lib/api";
import EnquiryStatusSelect from "@/components/EnquiryStatusSelect";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import LoginDevicesCard from "@/components/LoginDevicesCard";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [photoTarget, setPhotoTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([api.get("/properties/mine"), api.get("/enquiries")]);
      setProps(p.data);
      setEnquiries(e.data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">My dashboard</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Signed in as {user?.email}</p>
        </div>
        <Link to="/post-property">
          <Button className="h-12 rounded-lg font-bold hover:brightness-95" data-testid="dashboard-post-btn">
            <Plus className="h-4 w-4 mr-2" /> List a property
          </Button>
        </Link>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[
          { label: "Active listings", value: props.length },
          { label: "Enquiries received", value: enquiries.length },
          { label: "Site visits requested", value: enquiries.filter((e) => e.visited).length },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border p-6">
            <p className="font-display text-3xl">{s.value}</p>
            <p className="mt-1 text-[13px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings" className="mt-10">
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar rounded-full bg-muted p-1">
          <TabsTrigger value="listings" className="rounded-full px-5" data-testid="tab-listings">My listings</TabsTrigger>
          <TabsTrigger value="enquiries" className="rounded-full px-5" data-testid="tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="account" className="rounded-full px-5" data-testid="tab-account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-7">
          {props.length === 0 ? (
            <div className="rounded-2xl border border-border p-10">
              <p className="font-display text-xl">No listings yet</p>
              <p className="mt-2 text-sm text-muted-foreground">Post your first property to start receiving enquiries.</p>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="my-listings">
              {props.map((p) => (
                <div key={p.property_id} className="flex flex-col sm:flex-row gap-5 rounded-2xl border border-border p-4">
                  <img src={propertyImage(p)} alt={p.title} className="h-32 w-full sm:w-52 rounded-xl object-cover" />
                  <div className="flex-1">
                    <Link to={`/property/${p.property_id}`} className="font-display text-[15px] hover:underline">
                      {p.title}
                    </Link>
                    <p className="mt-1 text-[13px] text-muted-foreground">
                      {p.locality ? `${p.locality}, ` : ""}
                      {p.city} · {p.views || 0} views
                    </p>
                    <p className="mt-2 text-[15px] font-bold">{formatPrice(p.price, p.listing_type)}</p>
                  </div>
                  <div className="flex flex-row gap-1 sm:flex-col">
                    <Link to={`/post-property?edit=${p.property_id}`}>
                      <Button variant="ghost" size="sm" className="rounded-lg" data-testid={`edit-listing-${p.property_id}`} title="Edit listing details">
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="self-start rounded-lg"
                      onClick={() => setPhotoTarget(p)}
                      data-testid={`manage-photos-${p.property_id}`}
                      title="Manage & reorder photos"
                    >
                      <Images className="h-4 w-4 mr-1.5" /> Photos
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="self-start rounded-lg text-destructive"
                      onClick={() => remove(p.property_id)}
                      data-testid={`delete-listing-${p.property_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="enquiries" className="mt-7">
          <div className="mb-4 flex justify-end">
            <a href={`${BACKEND_URL}/api/enquiries/export`} data-testid="dashboard-export-excel-button">
              <Button variant="outline" className="h-11 rounded-lg font-semibold">
                <Download className="h-4 w-4 mr-2" /> Download Excel
              </Button>
            </a>
          </div>
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table data-testid="enquiries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Visited</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Following up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-muted-foreground">No enquiries yet.</TableCell>
                  </TableRow>
                ) : (
                  enquiries.map((e) => (
                    <TableRow key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`}>
                      <TableCell className="text-xs">{(e.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-sm font-semibold">{e.name}</TableCell>
                      <TableCell className="text-sm">{e.phone}</TableCell>
                      <TableCell className="text-sm">{e.property_title || "General enquiry"}</TableCell>
                      <TableCell className="text-sm">{e.visited ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <EnquiryStatusSelect
                          enquiry={e}
                          onUpdated={(u) => setEnquiries((prev) => prev.map((x) => (x.enquiry_id === u.enquiry_id ? u : x)))}
                        />
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`enquiry-assignee-${e.enquiry_id}`}>
                        {e.assigned_to_name || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="account" className="mt-7 space-y-6">
          <ProfileCard />
          <ChangePasswordCard />
          <LoginDevicesCard />
        </TabsContent>
      </Tabs>

      {photoTarget && (
        <PhotoManagerDialog
          property={photoTarget}
          open={!!photoTarget}
          onOpenChange={(v) => !v && setPhotoTarget(null)}
          onSaved={(updated) => {
            setProps((prev) => prev.map((x) => (x.property_id === updated.property_id ? updated : x)));
            setPhotoTarget(null);
          }}
        />
      )}
    </div>
  );
}
